import ChatService from "@/src/api/ChatService.js";

const MAX_OPEN_CHATS = 3;
const STORAGE_KEY = "embedded_chats_open";

function saveToStorage(openChats) {
  try {
    const data = openChats.map((c) => ({
      chatId: c.chat?.id,
      expanded: c.expanded,
    }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function normalizeId(id) {
  return String(id ?? "").replace(/^group_/, "");
}

function findEntry(openChats, chatId) {
  const normalized = normalizeId(chatId);
  return openChats.find((c) => normalizeId(c.chat?.id) === normalized);
}

function findEntryIndex(openChats, chatId) {
  const normalized = normalizeId(chatId);
  return openChats.findIndex((c) => normalizeId(c.chat?.id) === normalized);
}

export default {
  namespaced: true,

  state: {
    // Array of open chat objects: { chat, messages, loading, expanded, groupMembers, totalMemberCount }
    openChats: [],
  },

  getters: {
    openChats: (state) => state.openChats,
    openChatIds: (state) => state.openChats.map((c) => c.chat?.id),
    isChatOpen: (state) => (chatId) =>
      findEntry(state.openChats, chatId) !== undefined,
  },

  mutations: {
    OPEN_CHAT(state, chat) {
      const existingIndex = findEntryIndex(state.openChats, chat.id);
      if (existingIndex !== -1) {
        state.openChats[existingIndex].expanded = true;
        saveToStorage(state.openChats);
        return;
      }
      if (state.openChats.length >= MAX_OPEN_CHATS) {
        state.openChats.shift();
      }
      state.openChats.push({
        chat,
        messages: [],
        loading: false,
        loadingMore: false,
        hasMore: true,
        fetchedCount: 0,
        expanded: true,
        groupMembers: [],
        totalMemberCount: 0,
        threadOpen: false,
        threadId: null,
        threadMessages: [],
        threadLoading: false,
      });
      saveToStorage(state.openChats);
    },

    CLOSE_CHAT(state, chatId) {
      const normalized = normalizeId(chatId);
      state.openChats = state.openChats.filter(
        (c) => normalizeId(c.chat?.id) !== normalized,
      );
      saveToStorage(state.openChats);
    },

    TOGGLE_CHAT(state, chatId) {
      const entry = findEntry(state.openChats, chatId);
      if (entry) {
        entry.expanded = !entry.expanded;
        saveToStorage(state.openChats);
      }
    },

    SET_MESSAGES(state, { chatId, messages }) {
      const entry = findEntry(state.openChats, chatId);
      if (entry) {
        entry.messages = messages;
        entry.fetchedCount = messages.length;
      }
    },

    ADD_MESSAGE(state, { chatId, message }) {
      if (!message || !message.id) return;
      const entry = findEntry(state.openChats, chatId);
      if (entry) {
        const exists = entry.messages.some((m) => String(m.id) === String(message.id));
        if (!exists) {
          entry.messages = [...entry.messages, message];
        }
      }
    },

    DELETE_MESSAGE(state, { chatId, messageId }) {
      const entry = findEntry(state.openChats, chatId);
      if (entry) {
        entry.messages = entry.messages.filter((m) => String(m.id) !== String(messageId));
        entry.threadMessages = entry.threadMessages.filter((m) => String(m.id) !== String(messageId));
      }
    },

    UPDATE_MESSAGE(state, { chatId, message }) {
      if (!message || !message.id) return;
      const entry = findEntry(state.openChats, chatId);
      if (entry) {
        const msgId = String(message.id);
        const idx = entry.messages.findIndex((m) => String(m.id) === msgId);
        if (idx !== -1) {
          const updated = [...entry.messages];
          updated[idx] = { ...updated[idx], ...message };
          entry.messages = updated;
        }
        const tIdx = entry.threadMessages.findIndex((m) => String(m.id) === msgId);
        if (tIdx !== -1) {
          const updated = [...entry.threadMessages];
          updated[tIdx] = { ...updated[tIdx], ...message };
          entry.threadMessages = updated;
        }
      }
    },

    UPDATE_REACTION(state, { messageId, emoji, delta }) {
      // Update across all open chats
      state.openChats.forEach((entry) => {
        const updateInArray = (arr) => {
          const idx = arr.findIndex((m) => String(m.id) === String(messageId));
          if (idx === -1) return arr;
          const msg = arr[idx];
          const reactions = { ...(msg.reactions || {}) };
          const current = reactions[emoji] || 0;
          const newCount = Math.max(0, current + delta);
          if (newCount > 0) {
            reactions[emoji] = newCount;
          } else {
            delete reactions[emoji];
          }
          const updated = [...arr];
          updated[idx] = { ...msg, reactions };
          return updated;
        };
        entry.messages = updateInArray(entry.messages);
        entry.threadMessages = updateInArray(entry.threadMessages);
      });
    },

    UPDATE_REPLY_COUNT(state, { chatId, threadId, delta }) {
      const entry = findEntry(state.openChats, chatId);
      if (entry) {
        const idx = entry.messages.findIndex(
          (m) => String(m.id) === String(threadId),
        );
        if (idx !== -1) {
          const updated = [...entry.messages];
          updated[idx] = {
            ...updated[idx],
            reply_count: Math.max(0, (updated[idx].reply_count || 0) + delta),
          };
          entry.messages = updated;
        }
      }
    },

    SET_LOADING(state, { chatId, loading }) {
      const entry = findEntry(state.openChats, chatId);
      if (entry) {
        entry.loading = loading;
      }
    },

    PREPEND_MESSAGES(state, { chatId, messages }) {
      const entry = findEntry(state.openChats, chatId);
      if (entry) {
        const existingIds = new Set(entry.messages.map((m) => String(m.id)));
        const newMessages = messages.filter((m) => !existingIds.has(String(m.id)));
        entry.messages = [...newMessages, ...entry.messages];
        entry.fetchedCount += messages.length;
      }
    },

    SET_LOADING_MORE(state, { chatId, loading }) {
      const entry = findEntry(state.openChats, chatId);
      if (entry) {
        entry.loadingMore = loading;
      }
    },

    SET_HAS_MORE(state, { chatId, hasMore }) {
      const entry = findEntry(state.openChats, chatId);
      if (entry) {
        entry.hasMore = hasMore;
      }
    },

    SET_GROUP_MEMBERS(state, { chatId, members, totalCount }) {
      const entry = findEntry(state.openChats, chatId);
      if (entry) {
        entry.groupMembers = members;
        entry.totalMemberCount = totalCount;
      }
    },

    UPDATE_CHAT_INFO(state, { chatId, updates }) {
      const entry = findEntry(state.openChats, chatId);
      if (entry && entry.chat) {
        entry.chat = { ...entry.chat, ...updates };
      }
    },

    REPLACE_CHAT(state, { oldChatId, newChat }) {
      const normalized = normalizeId(oldChatId);
      const index = state.openChats.findIndex(
        (c) => normalizeId(c.chat?.id) === normalized,
      );
      if (index !== -1) {
        state.openChats[index].chat = newChat;
      }
    },

    OPEN_THREAD(state, { chatId, threadId }) {
      const entry = findEntry(state.openChats, chatId);
      if (entry) {
        entry.threadOpen = true;
        entry.threadId = threadId;
        entry.threadMessages = [];
        entry.threadLoading = true;
      }
    },

    CLOSE_THREAD(state, { chatId }) {
      const entry = findEntry(state.openChats, chatId);
      if (entry) {
        entry.threadOpen = false;
        entry.threadId = null;
        entry.threadMessages = [];
        entry.threadLoading = false;
      }
    },

    SET_THREAD_MESSAGES(state, { chatId, messages }) {
      const entry = findEntry(state.openChats, chatId);
      if (entry) {
        entry.threadMessages = messages;
      }
    },

    ADD_THREAD_MESSAGE(state, { chatId, message }) {
      if (!message || !message.id) return;
      const entry = findEntry(state.openChats, chatId);
      if (entry) {
        const exists = entry.threadMessages.some((m) => String(m.id) === String(message.id));
        if (!exists) {
          entry.threadMessages = [...entry.threadMessages, message];
        }
      }
    },

    SET_THREAD_LOADING(state, { chatId, loading }) {
      const entry = findEntry(state.openChats, chatId);
      if (entry) {
        entry.threadLoading = loading;
      }
    },
  },

  actions: {
    openChat({ commit, dispatch, getters }, chat) {
      const alreadyOpen = getters.isChatOpen(chat.id);
      commit("OPEN_CHAT", chat);
      if (!alreadyOpen && !chat.is_placeholder) {
        dispatch("fetchMessages", chat.id);
        dispatch("fetchGroupMembers", chat.id);
      }
    },

    closeChat({ commit }, chatId) {
      commit("CLOSE_CHAT", chatId);
    },

    restoreChats({ commit, dispatch, rootGetters }) {
      const saved = loadFromStorage();
      if (!saved || saved.length === 0) return;

      const groups = rootGetters["chats/groups"] || [];
      saved.forEach(({ chatId, expanded }) => {
        if (!chatId) return;
        const normalizedSaved = normalizeId(chatId);
        const group = groups.find((g) => normalizeId(g.id) === normalizedSaved);
        if (group) {
          commit("OPEN_CHAT", group);
          if (!expanded) {
            commit("TOGGLE_CHAT", group.id);
          }
          dispatch("fetchMessages", group.id);
          dispatch("fetchGroupMembers", group.id);
        }
      });
    },

    toggleChat({ commit, state, dispatch }, chatId) {
      commit("TOGGLE_CHAT", chatId);
      const entry = findEntry(state.openChats, chatId);
      if (entry?.expanded && entry.messages.length > 0) {
        ChatService.markAsRead(chatId).catch(() => {});
        dispatch("chats/updateUnreadCount", { groupId: chatId, unreadCount: 0 }, { root: true });
      }
    },

    async fetchMessages({ commit, state, dispatch }, chatId) {
      const entry = findEntry(state.openChats, chatId);
      if (!entry) return;

      commit("SET_LOADING", { chatId, loading: true });
      try {
        const response = await ChatService.fetchGroupMessages(chatId, 50, 0);
        if (response?.success && response?.messages) {
          commit("SET_MESSAGES", { chatId, messages: response.messages });
          commit("SET_HAS_MORE", {
            chatId,
            hasMore: response.pagination?.has_more ?? false,
          });

          if (response.messages.length > 0 && entry.expanded) {
            ChatService.markAsRead(chatId).catch(() => {});
            dispatch("chats/updateUnreadCount", { groupId: chatId, unreadCount: 0 }, { root: true });
          }
        }
      } catch (error) {
        console.error("Error fetching embedded chat messages:", error);
      } finally {
        commit("SET_LOADING", { chatId, loading: false });
      }
    },

    async fetchOlderMessages({ commit, state }, chatId) {
      const entry = findEntry(state.openChats, chatId);
      if (!entry || entry.loadingMore || !entry.hasMore) return;

      commit("SET_LOADING_MORE", { chatId, loading: true });
      try {
        const offset = entry.fetchedCount;
        const response = await ChatService.fetchGroupMessages(chatId, 50, offset);
        if (response?.success && response?.messages) {
          commit("PREPEND_MESSAGES", { chatId, messages: response.messages });
          commit("SET_HAS_MORE", {
            chatId,
            hasMore: response.pagination?.has_more ?? false,
          });
        }
      } catch (error) {
        console.error("Error fetching older embedded chat messages:", error);
      } finally {
        commit("SET_LOADING_MORE", { chatId, loading: false });
      }
    },

    async fetchGroupMembers({ commit, state }, chatId) {
      const entry = findEntry(state.openChats, chatId);
      if (!entry) return;

      try {
        const response = await ChatService.fetchGroupMembers(chatId, {
          limit: 1000,
          offset: 0,
        });
        if (response?.success && response?.members) {
          commit("SET_GROUP_MEMBERS", {
            chatId,
            members: response.members,
            totalCount:
              response?.pagination?.total_count ?? response.members.length,
          });
        }
      } catch (error) {
        console.error("Error fetching embedded chat members:", error);
      }
    },

    openThread({ commit, dispatch }, { chatId, threadId }) {
      commit("OPEN_THREAD", { chatId, threadId });
      dispatch("fetchThreadMessages", { chatId, threadId });
    },

    closeThread({ commit }, { chatId }) {
      commit("CLOSE_THREAD", { chatId });
    },

    async fetchThreadMessages({ commit, state }, { chatId, threadId }) {
      const entry = findEntry(state.openChats, chatId);
      if (!entry) return;

      commit("SET_THREAD_LOADING", { chatId, loading: true });
      try {
        const [repliesResponse, rootResponse] = await Promise.all([
          ChatService.fetchThreadReplies(chatId, threadId, 50, 0),
          ChatService.fetchMessageById(threadId, chatId),
        ]);

        // Guard against stale response if thread was closed or switched
        const currentEntry = findEntry(state.openChats, chatId);
        if (!currentEntry || !currentEntry.threadOpen || currentEntry.threadId !== threadId) return;

        const messages = rootResponse?.success && rootResponse?.message
          ? [rootResponse.message, ...(repliesResponse?.messages || [])]
          : repliesResponse?.messages || [];

        commit("SET_THREAD_MESSAGES", { chatId, messages });
        commit("SET_THREAD_LOADING", { chatId, loading: false });
      } catch (error) {
        console.error("Error fetching embedded thread messages:", error);
        // Only reset loading if this thread is still active
        const currentEntry = findEntry(state.openChats, chatId);
        if (currentEntry && currentEntry.threadId === threadId) {
          commit("SET_THREAD_LOADING", { chatId, loading: false });
        }
      }
    },
  },
};
